import React, { useEffect } from "react";

import { ParentSize } from "@visx/responsive";
import { groupBy, isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import {
  getSum,
  getWeightedAverage,
} from "@app/analytics/MeetingPolicy/meetingPolicyUtils";
import useMeetingPolicy from "@app/analytics/MeetingPolicy/useMeetingPolicy";
import { multiParticipantMeeting } from "@app/analytics/meetops/filters";
import { getMetricMetaById } from "@app/analytics/meetops/metrics";
import { MeetOpsQuery } from "@app/analytics/meetops/types";
import { DateRange } from "@app/common/types";

import { Row, Column } from "@rui/Stack";

import DonutChart from "./DonutChart";
import getColorFromScore from "./getColorFromScore";
import MeetingPolicyModule from "./MeetingPolicyModule";
import { meetingSize as meetingSizeAvgs } from "./readAverages";
import { MeetingPolicyMetric, Size } from "./types";

interface DonutBoxProps {
  value: { value: number; formattedValue: string };
  label: string;
  numMeetings: { value: number; formattedValue: string };
  empty?: boolean;
  loading?: boolean;
  showScore: boolean;
  showMeetingCount: boolean;
  width: number;
  height: number;
  markerValue?: number | null;
  tooltip: React.ReactNode;
}

const SIZES: Size[] = ["small", "medium", "large"];
const DONUT_HORIZONTAL_PADDING = 16;
const DONUT_VERTICAL_PADDING = 64;

const DonutBox = ({
  empty = false,
  value = { value: 0, formattedValue: "-" },
  label,
  numMeetings = { value: 0, formattedValue: "0" },
  loading = false,
  showScore,
  showMeetingCount,
  width,
  height,
  markerValue,
  tooltip,
}: DonutBoxProps) => {
  return (
    <Column
      variant="center"
      spacing={1}
      sx={{ px: `${DONUT_HORIZONTAL_PADDING}px`, width: "100%" }}
    >
      <Row
        variant="center"
        sx={{ bgcolor: "shades.ghost.300", px: 2, py: 1, borderRadius: "8px" }}
      >
        <Typography variant="bodySecondaryMed" color="shades.neutral.600">
          {label}
        </Typography>
      </Row>
      <DonutChart
        loading={loading}
        empty={empty}
        width={width - DONUT_HORIZONTAL_PADDING * 2}
        height={height - DONUT_VERTICAL_PADDING}
        label={value.formattedValue ?? "-"}
        value={value.value ?? 0}
        subLabel={`[${numMeetings.value}]`}
        showValue={showScore}
        showSubLabel={showMeetingCount}
        fill={getColorFromScore(value.value)}
        markerValue={markerValue}
        tooltip={tooltip}
      />
    </Column>
  );
};

const getHighestAndLowest = (
  data: any[],
  selectedMetric: MeetingPolicyMetric
) => {
  const keys = Object.keys(data) as Size[];
  const values = Object.values(data).map((v) => v[selectedMetric].value);
  const highest = keys[values.indexOf(Math.max(...values))];
  const lowest = keys[values.indexOf(Math.min(...values))];
  return { highest, lowest };
};

const metricsBySize = (results: any[] | undefined) => {
  const groupedResults = groupBy(
    (r) => r.meetingSizeBucket.formattedValue,
    results
  );
  const averaged = Object.entries(groupedResults).map(([k, v]) => {
    return {
      [k]: {
        sentiment: getWeightedAverage(v, "sentiment"),
        engagement: getWeightedAverage(v, "engagement"),
        meetingScore: getWeightedAverage(v, "meetingScore"),
        onTimeRate: getWeightedAverage(v, "onTimeRate"),
        numMeetings: getSum(v, "meetingCount"),
      },
    };
  });

  return Object.assign({}, ...averaged);
};

const getQuery = (
  dateRange: DateRange | null,
  peopleValue: "only_me" | "everyone",
  teamId?: string,
  userId?: string
): MeetOpsQuery | null => {
  if (!dateRange) return null;

  return {
    dateRanges: [dateRange],
    dimensions: ["participantCount"],
    metrics: [
      "engagement",
      "meetingCount",
      "sentiment",
      "meetingScore",
      "observedParticipantCount",
      "onTimeRate",
      "meetingSizeBucket",
    ],
    filters: [multiParticipantMeeting()],
    people: peopleValue,
    userId,
    teamId,
  };
};

const SIZE_LABELS = {
  small: "1 on 1",
  medium: "3-7 people",
  large: "8+ people",
};

const MeetingSizeModule = () => {
  const { filterBy, teamId, userId } = useRollupContext();
  const viewingIndividualMetrics =
    filterBy === "individual" || filterBy === "me";
  const peopleValue = viewingIndividualMetrics ? "only_me" : "everyone";

  const { startDate, endDate, toggleState, selectedMetric, reportNoData } =
    useMeetingPolicy();
  const metricMeta = getMetricMetaById(selectedMetric);
  const queryDateRange = startDate && endDate ? { startDate, endDate } : null;

  const meetingSizeQuery = useMetricsQuery(
    getQuery(queryDateRange, peopleValue, teamId, userId),
    { staleTime: 30 * 60 * 1000 }
  );
  const meetingSizeResult = meetingSizeQuery.data?.results[0].data;

  useEffect(() => {
    if (meetingSizeQuery.status === "success" && meetingSizeResult == null) {
      reportNoData();
    }
  }, [meetingSizeResult, meetingSizeQuery.status]);

  const loading = meetingSizeQuery.isInitialLoading;

  const data = metricsBySize(meetingSizeResult) ?? null;
  const empty = isEmpty(data);
  const { highest, lowest } = getHighestAndLowest(data, selectedMetric);

  return (
    <MeetingPolicyModule
      title="Meeting Size"
      highest={empty ? "-" : loading ? "Loading..." : SIZE_LABELS[highest]}
      lowest={empty ? "-" : loading ? "Loading..." : SIZE_LABELS[lowest]}
    >
      <ParentSize>
        {(parent) => {
          return (
            <Row
              alignItems="center"
              justifyContent="space-evenly"
              sx={{ width: "100%", height: "100%" }}
            >
              {SIZES.map((size) => {
                const donutData = data[size];
                const readAvgValue = meetingSizeAvgs[size][selectedMetric];
                const noData = empty || !donutData;

                return (
                  <DonutBox
                    width={parent.width / 3}
                    height={parent.height}
                    key={size}
                    value={noData ? { value: 0 } : donutData[selectedMetric]}
                    label={SIZE_LABELS[size]}
                    numMeetings={noData ? { value: 0 } : donutData.numMeetings}
                    loading={loading}
                    empty={empty}
                    showScore={toggleState.chartLabels}
                    showMeetingCount={toggleState.meetingCount}
                    markerValue={
                      toggleState.readAverage
                        ? metricMeta.toValue(readAvgValue)
                        : undefined
                    }
                    tooltip={
                      <ul
                        style={{
                          listStyle: "none",
                          padding: 0,
                          margin: 0,
                          fontSize: "0.75rem",
                        }}
                      >
                        <li>
                          {metricMeta.displayName}:{" "}
                          {donutData?.[selectedMetric].formattedValue ?? "-"}
                        </li>
                        {donutData?.numMeetings && (
                          <li>Meetings: {donutData.numMeetings.value}</li>
                        )}
                        {toggleState.readAverage && (
                          <li>
                            Read Avg:{" "}
                            {metricMeta.toFormat(
                              metricMeta.toValue(readAvgValue)
                            )}
                          </li>
                        )}
                      </ul>
                    }
                  />
                );
              })}
            </Row>
          );
        }}
      </ParentSize>
    </MeetingPolicyModule>
  );
};

export default MeetingSizeModule;
